import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import en, { type Dictionary, type TranslationKey } from "./dictionaries/en";

/**
 * 管理後台的 i18n
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼後台也需要這個 ────────────────────────────────────
 *
 * App.tsx 原本的註解寫著「管理後台是內部工具，只有中文」。
 * 那在只有一個人管的時候成立，但這是一個七市場的平台 ——
 * 找日本或西班牙的在地審核人員時，「只有中文」就變成
 * 「只能請看得懂中文的人」。那是產品的限制，不是技術的。
 *
 * ── 和前台的差別 ────────────────────────────────────────────
 *
 * 前台的語系來自網址（/tw/en/...），因為那影響 SEO ——
 * 每個語言版本需要自己的可索引網址。
 *
 * 後台沒有 SEO，也沒有市場前綴（管理員本來就跨市場），
 * 所以語系只是一個個人偏好，存在 localStorage 就夠了。
 * 不需要 Provider 以外的任何東西。
 *
 * ── 字典為什麼不跟前台共用 ──────────────────────────────────
 *
 * 兩邊的詞彙不一樣。前台說「案件」，後台說「案件管理」；
 * 前台的「停用」不存在，後台每天都在用。共用一份字典的話，
 * 前台的 bundle 會扛著一堆它永遠用不到的管理用語。
 */

/*
  十八種語言 —— 和前台同一份清單（packages/shared 的 SITE_LOCALES）。

  這裡不直接 import 那份清單，因為管理台不該依賴市場設定：
  它是跨市場的工具，而 SITE_LOCALES 的形狀是為了市場而設計的。
  兩邊同時改是刻意的重複，重複的代價比錯誤的依賴小。
*/
export const locales = [
  "en",
  "zh-TW",
  "ja",
  "ko",
  "es",
  "fr",
  "de",
  "pt-BR",
  "it",
  "nl",
  "pl",
  "tr",
  "id",
  "th",
  "vi",
  "ru",
  "ar",
  "hi",
] as const;
export type Locale = (typeof locales)[number];

/** 語言選單上顯示的名字 —— 一律用該語言自己的說法 */
// i18n-ignore-start：語言名一律用該語言自己的說法，不隨介面語系變動。
// 一個只看得懂泰文的人，在一串英文語言名裡找不到自己那一個。
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  "zh-TW": "繁體中文",
  ja: "日本語",
  ko: "한국어",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  "pt-BR": "Português (Brasil)",
  it: "Italiano",
  nl: "Nederlands",
  pl: "Polski",
  tr: "Türkçe",
  id: "Bahasa Indonesia",
  th: "ไทย",
  vi: "Tiếng Việt",
  ru: "Русский",
  ar: "العربية",
  hi: "हिन्दी",
};
// i18n-ignore-end

/** 由右往左書寫的語言 */
const RTL = new Set<string>(["ar"]);

/*
  ── 為什麼字典是動態 import ────────────────────────────────

  十八本字典各 286 條。全部靜態 import 的話，一個只看繁體中文的
  管理員，每次開後台都要下載另外十七種語言。

  改成動態之後只有實際用到的那一本會被下載，而它是一個獨立的
  chunk —— 換語言時才去拿。

  en 例外，它是靜態 import 的：t() 查不到 key 時要退回英文，
  而那件事必須是同步的。順帶讓「還沒載好」有東西可以顯示。
*/
const LOADERS: Record<Exclude<Locale, "en">, () => Promise<{ default: Partial<Dictionary> }>> = {
  "zh-TW": () => import("./dictionaries/zh-TW"),
  ja: () => import("./dictionaries/ja"),
  ko: () => import("./dictionaries/ko"),
  es: () => import("./dictionaries/es"),
  fr: () => import("./dictionaries/fr"),
  de: () => import("./dictionaries/de"),
  "pt-BR": () => import("./dictionaries/pt-BR"),
  it: () => import("./dictionaries/it"),
  nl: () => import("./dictionaries/nl"),
  pl: () => import("./dictionaries/pl"),
  tr: () => import("./dictionaries/tr"),
  id: () => import("./dictionaries/id"),
  th: () => import("./dictionaries/th"),
  vi: () => import("./dictionaries/vi"),
  ru: () => import("./dictionaries/ru"),
  ar: () => import("./dictionaries/ar"),
  hi: () => import("./dictionaries/hi"),
};

/*
  已經載好的字典，放在模組層而不是元件狀態裡 ——
  換回去一個看過的語言時不必再下載一次。
*/
const LOADED = new Map<Locale, Partial<Dictionary>>([["en", en]]);

/**
 * 先把字典載好。
 *
 * 給 main.tsx 在掛載之前呼叫：管理台的語系存在 localStorage，
 * 讀得到的時候就該直接用那一本開場，而不是先畫一次英文再換掉。
 *
 * 認不得的語系什麼都不做 —— 呼叫端不需要先驗證。
 */
export async function preloadDictionary(locale: string): Promise<void> {
  if (LOADED.has(locale as Locale)) return;
  const load = LOADERS[locale as Exclude<Locale, "en">];
  if (!load) return;
  try {
    const mod = await load();
    LOADED.set(locale as Locale, mod.default);
  } catch {
    /* 載不到就退回英文 —— t() 本來就會 fallback */
  }
}

const STORAGE_KEY = "colourdev.admin.locale";

type Vars = Record<string, string | number>;

interface I18nValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: TranslationKey, vars?: Vars) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );
}

/**
 * 預設語系：存過的偏好 → 瀏覽器語言 → 繁體中文。
 *
 * 最後的預設是繁體中文而不是英文 —— 現有的管理員是中文使用者，
 * 換成英文預設會讓一個原本能用的後台突然變成外文。
 */
export function detectLocale(): Locale {
  if (typeof window === "undefined") return "zh-TW";

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && isLocale(stored)) return stored;

  for (const candidate of navigator.languages ?? [navigator.language]) {
    if (isLocale(candidate)) return candidate;
    const base = candidate.split("-")[0]?.toLowerCase();
    if (base === "zh") return "zh-TW";
    const match = locales.find((l) => l.split("-")[0].toLowerCase() === base);
    if (match) return match;
  }
  return "zh-TW";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  /*
    初始值取已經載好的那一本。

    main.tsx 會在掛載之前先 preload，所以正常情況下這裡拿到的
    就是對的字典，第一次繪製就是對的語言。拿不到才退回英文 ——
    下面的 effect 會把它補上。
  */
  const [dictionary, setDictionary] = useState<Partial<Dictionary>>(
    () => LOADED.get(locale) ?? en,
  );

  useEffect(() => {
    const cached = LOADED.get(locale);
    if (cached) {
      setDictionary(cached);
      return;
    }

    let cancelled = false;
    const load = LOADERS[locale as Exclude<Locale, "en">];
    if (!load) return;

    void load()
      .then((mod) => {
        LOADED.set(locale, mod.default);
        if (!cancelled) setDictionary(mod.default);
      })
      .catch(() => {
        /* 載不到就繼續用目前這本 —— 比一個當掉的畫面好 */
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = locale;
    /*
      阿拉伯文由右往左。

      不設 dir 的話整個版面還是左起，而阿拉伯文的介面
      左起讀起來就像把中文由下往上排 —— 字都對，讀不了。
    */
    document.documentElement.dir = RTL.has(locale) ? "rtl" : "ltr";
  }, [locale]);

  const t = useCallback(
    (key: TranslationKey, vars?: Vars) => {
      /*
        查不到就退回英文，再退回 key 本身。

        這讓新語言可以只翻一半就先上線 —— 沒翻到的地方顯示英文，
        總比顯示一個空字串或程式碼裡的 key 好。
      */
      const value = dictionary[key] ?? en[key] ?? key;
      return interpolate(value, vars);
    },
    /*
      dictionary 必須在依賴裡。

      少了它，換語言時 t() 會關閉在舊字典上直到整頁重新載入 ——
      前台正好犯過這個錯（commit 75c82a2）。字典從常數變成會變的
      東西的那一刻，所有讀它的 memo/callback 都要跟著改。
    */
    [dictionary, locale],
  );

  const value = useMemo<I18nValue>(
    () => ({ locale, setLocale: setLocaleState, t }),
    [locale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n 必須在 I18nProvider 裡面使用");  // i18n-ignore：給開發者看的例外訊息
  return ctx;
}

/**
 * 把伺服器的錯誤回應變成一句給人看的話。
 * ═══════════════════════════════════════════════════════════════
 *
 * 服務端回的 error.message 是英文、寫給看 log 的人；error.key
 * 才是要顯示給操作者的那一句，由這裡查字典翻出來。
 *
 * 三層退路，缺哪一層都還有下一層：
 *   1. key 查得到 → 用翻譯
 *   2. 有 message → 用它（還沒改成 key 的呼叫點照常運作）
 *   3. 都沒有   → 呼叫端給的 fallback
 *
 * 不用 hook 而是把 t 當參數：這個函式會在 catch 區塊與
 * async 函式裡被呼叫，那些地方不是 render 階段，hook 不合法。
 */
export function serverError(
  payload: unknown,
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
  fallback: string,
): string {
  const err = (
    payload as {
      error?: { key?: string; message?: string; params?: Record<string, string | number> };
    } | null
  )?.error;
  if (!err) return fallback;

  if (err.key) {
    const translated = t(err.key as TranslationKey, err.params);
    // t() 查不到會原樣回傳 key。那種字串不該出現在畫面上
    if (translated !== err.key) return translated;
  }
  return err.message ?? fallback;
}

export type { TranslationKey };
